import { maybe } from '@passionware/monads';
import { parseWithDecoratedError } from 'v4/entities/common/parseWithDecoratedError';
import { convertBooleanFilterToApi } from 'v5/platform/filters/filterConverters';
import { z } from 'zod';
import { Resource, Shortlist } from '../../../__types__';
import { ApiClient } from '../../../lib/api/ApiClient.types';
import { convertCoreFieldFiltersToHttp } from '../../core/core-fields/core-fields-filters.http-converters';
import { convertCustomFieldFiltersToHttp } from '../../core/custom-fields/custom-fields-filters.http-converters';
import { MergeServices, WithServices } from '../../platform/ts/services';
import { WithMetastructureMappingService } from '../../services/internal/MetastructureMappingService/MetastructureMappingService';
import {
  BulkArchiveRequest,
  ShortlistApi,
  ShortlistQuery,
  ShortlistResourcesSearchQuery
} from './shortlist.api';
import { shortlist$, shortlistFromHttp } from './shortlist.http.schema';
import { getResourceListRequest } from '../resource/resource.api.http';

async function searchShortlists(
  client: ApiClient,
  services: MergeServices<[WithMetastructureMappingService]>,
  query: ShortlistQuery
): Promise<{ items: Shortlist[]; count: number }> {
  // Get field type mappings for custom fields
  const fieldMappings =
    await services.metastructureMappingService.getAllFieldNameTypeMappings();

  const filters = {
    published: maybe.map(query.filters.published, convertBooleanFilterToApi),
    ...convertCoreFieldFiltersToHttp(query.filters),
    // Convert custom field filters using the field type mappings
    ...convertCustomFieldFiltersToHttp(fieldMappings, query.filters)
  };

  // Map frontend sort fields to backend field names
  const mapSortFieldToApi = (field: string): string => {
    const fieldMap: Record<string, string> = {
      createdAt: 'created_at',
      updatedAt: 'updated_at',
      title: "attributes->>'title'",
      publishedAt: 'published_at'
    };
    return fieldMap[field] ?? field;
  };

  const requestBody: Record<string, unknown> = {
    query: query.search,
    p: query.page.page + 1,
    count: query.page.pageSize,
    order_by: query.sort
      ? {
          field: mapSortFieldToApi(query.sort.field),
          direction: query.sort.order.toUpperCase()
        }
      : {
          field: 'created_at',
          direction: 'DESC'
        },
    includeResource: true,
    filters,
    archived: query.filters?.archived?.value ? true : false
  };

  const response = await client.post('/search/shortlists', {
    data: requestBody,
    token: true
  });

  const responseSchema = z.object({
    results: z.array(shortlist$),
    paging: z.object({
      total: z.number(),
      page: z.number(),
      count_per_page: z.number()
    })
  });

  const parsed = parseWithDecoratedError(responseSchema, response);
  return {
    items: parsed.results.map(shortlistFromHttp),
    count: parsed.paging.total
  };
}

export function createShortlistApi(
  config: { client: ApiClient } & WithServices<
    [WithMetastructureMappingService]
  >
): ShortlistApi {
  const { client, services } = config;
  return {
    getShortlists: async query => {
      return searchShortlists(client, services, query);
    },

    getShortlist: async id => {
      const response = await client.get(`/shortlists/${id}`, { token: true });

      const parsed = parseWithDecoratedError(shortlist$, response);
      return shortlistFromHttp(parsed);
    },

    searchShortlistResources: async (query: ShortlistResourcesSearchQuery) => {
      // Get field type mappings for custom fields
      const fieldMappings =
        await services.metastructureMappingService.getAllFieldNameTypeMappings();

      const requestData = getResourceListRequest(query, fieldMappings);

      const response = await client.post<{
        results: Resource['id'][];
        paging: { total: number; page: number };
      }>(`/shortlists/${query.shortlistId}/contacts/search`, {
        data: requestData,
        token: true
      });

      return {
        items: response.results,
        count: response.paging.total
      };
    },

    bulkArchive: async (request: BulkArchiveRequest) => {
      // Archive shortlists by updating each one with archived: true
      await Promise.all(
        request.ids.map(id =>
          client.put(`/shortlists/${id}`, {
            data: { archived: true },
            token: true
          })
        )
      );
    },

    bulkUnarchive: async (request: BulkArchiveRequest) => {
      // Unarchive shortlists by updating each one with archived: false
      await Promise.all(
        request.ids.map(id =>
          client.put(`/shortlists/${id}`, {
            data: { archived: false },
            token: true
          })
        )
      );
    }
  };
}
